import { useState } from "react";
import type { FyaiDownloadLinks } from "@/hooks/useFyaiConfig";
import { useFyaiConfig } from "@/hooks/useFyaiConfig";
import { useFyaiSeo } from "@/hooks/useFyaiSeo";
import {
  fyaiPlatforms,
  fyaiShotCharacter,
  fyaiShotChat,
  fyaiShotExplore,
  fyaiShotStory,
} from "@/mocks/fyai-data";

const steps = [
  {
    icon: "ri-file-download-line",
    title: "选择平台",
    desc: "根据设备选择 Android、iOS、HarmonyOS 或 Windows 版本。",
  },
  {
    icon: "ri-install-line",
    title: "安装应用",
    desc: "下载完成后按提示安装，首次启动请允许必要权限。",
  },
  {
    icon: "ri-login-circle-line",
    title: "登录账号",
    desc: "使用手机号或第三方账号登录，进度与记忆自动同步。",
  },
  {
    icon: "ri-chat-heart-line",
    title: "开启对话",
    desc: "挑选心动的角色，发出第一句问候，开启你的故事。",
  },
];

const heroShots = [
  { img: fyaiShotCharacter, wrap: "w-[96px] -rotate-6 md:w-[132px]" },
  { img: fyaiShotChat, wrap: "z-10 w-[126px] md:w-[176px]" },
  { img: fyaiShotStory, wrap: "w-[96px] rotate-6 md:w-[132px]" },
];

const platformShots: Record<string, string> = {
  android: fyaiShotExplore,
  ios: fyaiShotChat,
  harmonyos: fyaiShotCharacter,
  windows: fyaiShotStory,
};

const trustItems = [
  {
    icon: "ri-shield-check-line",
    title: "官方安全保证",
    desc: "安装包均由风月AI团队官方发布，经安全校验，无广告插件与恶意代码。",
  },
  {
    icon: "ri-refresh-line",
    title: "持续版本更新",
    desc: "新角色、新剧情与体验优化持续上线，风月AI官网下载始终是最新版本。",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "贴心客服支持",
    desc: "安装或使用中遇到问题，可通过页尾邮箱联系风月AI团队，我们会尽快回复。",
  },
];

export default function FyaiDownload() {
  const config = useFyaiConfig();
  useFyaiSeo({
    title: "风月AI官网下载-风月AI聊天软件下载-风月AI",
    keywords: "风月AI官网下载,风月AI聊天软件下载",
    description:
      "风月AI官网下载带来丰富多样的AI聊天体验，支持Android等多平台下载，快来风月AI开启情感陪伴新旅程吧。",
    path: "/download",
  });
  const [active, setActive] = useState("android");
  const activePlatform = fyaiPlatforms.find((platform) => platform.key === active) || fyaiPlatforms[0];
  const activeLink = config.downloadLinks[activePlatform.key as keyof FyaiDownloadLinks];
  const activeShot = platformShots[activePlatform.key] ?? fyaiShotExplore;

  return (
    <div className="flex min-h-screen w-full flex-col overflow-x-hidden bg-background-50">
      <main className="w-full flex-1">
        <section className="relative w-full overflow-hidden bg-primary-950 px-4 py-16 md:px-6 md:py-24">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-950 via-primary-900/70 to-primary-950" />
          <div className="absolute -left-20 top-0 h-64 w-64 rounded-full bg-primary-500/30 blur-3xl fyai-glow" />
          <div className="absolute -right-10 bottom-0 h-72 w-72 rounded-full bg-accent-500/20 blur-3xl fyai-glow" />

          <div className="relative z-10 grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-10">
            <div className="fyai-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full border border-accent-400/40 bg-primary-900/50 px-3 py-1.5 text-xs font-medium text-accent-300">
                <i className="ri-download-cloud-2-line" />
                官方下载中心
              </span>
              <h1 className="mt-5 font-heading text-4xl font-black text-background-50 md:text-5xl">
                风月AI官网下载
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-primary-100 md:text-base">
                这里是风月AI聊天软件的官方下载页面，AI风月的情感陪伴现在就能拥有。四大平台同一份陪伴，全部安装包均来自官方，安全无捆绑。
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-accent-500/40 bg-primary-900/60 px-4 py-2">
                  <i className="ri-link-m text-accent-400" />
                  <span className="font-heading text-sm font-bold text-accent-400 md:text-base">
                    {config.domain}
                  </span>
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-900/60 px-4 py-2 text-xs font-medium text-primary-100 md:text-sm">
                  <i className="ri-shield-check-line text-accent-400" />
                  官方正版 · 无捆绑
                </span>
              </div>
            </div>

            <div className="fyai-fade-up fyai-delay-1 flex items-end justify-center gap-2 md:gap-4">
              {heroShots.map((shot) => (
                <div
                  key={shot.img}
                  className={`rounded-[1.5rem] border border-background-50/20 bg-primary-950/60 p-1.5 md:rounded-[2rem] md:p-2.5 ${shot.wrap}`}
                >
                  <div className="overflow-hidden rounded-[1rem] border border-background-50/10 bg-foreground-950 md:rounded-[1.5rem]">
                    <img
                      src={shot.img}
                      alt="风月AI软件界面预览"
                      title="风月AI聊天软件"
                      className="block aspect-[9/16] w-full object-contain"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 py-14 md:px-6 md:py-20">
          <div className="mb-8 max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-900">
              <i className="ri-device-line" />
              选择平台
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              一个账号，四大平台
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              选择你的设备，即可获取对应的官方安装包，风月AI官网下载体验一键开启。
            </p>
          </div>

          <div className="flex w-full flex-wrap gap-2 rounded-full border border-background-200 bg-background-100 p-1.5 md:w-fit">
            {fyaiPlatforms.map((platform) => (
              <button
                key={platform.key}
                type="button"
                onClick={() => setActive(platform.key)}
                className={`inline-flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-medium transition-colors md:flex-none md:px-6 ${
                  active === platform.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-background-50"
                }`}
              >
                <i className={`${platform.icon} text-base`} />
                {platform.name}
              </button>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-8 rounded-card border border-background-200 bg-background-100 p-6 md:grid-cols-[1.2fr_1fr] md:p-10 lg:gap-14">
            <div className="flex flex-col justify-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                <i className={`${activePlatform.icon} text-3xl`} />
              </span>
              <h3 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {activePlatform.name} 版
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                {activePlatform.desc}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <span className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-900">
                  {activePlatform.meta}
                </span>
                <span className="rounded-full bg-accent-100 px-3 py-1.5 text-xs font-medium text-accent-900">
                  官方正版 · 无捆绑
                </span>
              </div>
              <a
                href={activeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 inline-flex w-fit cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 md:text-base"
              >
                <i className="ri-download-2-line" />
                下载 {activePlatform.name} 版
              </a>
              <p className="mt-3 text-xs text-foreground-500">点击后将在新页面打开官方下载链接</p>
            </div>

            <div className="flex items-center justify-center">
              <div className="rounded-[2rem] border border-background-200 bg-background-50 p-3">
                <div className="w-[200px] overflow-hidden rounded-[1.4rem] border border-background-200 bg-foreground-950 md:w-[230px]">
                  <img
                    src={activeShot}
                    alt={`风月AI${activePlatform.name}版界面预览`}
                    title={`风月AI聊天软件${activePlatform.name}版`}
                    className="block aspect-[9/16] w-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 px-4 py-14 md:px-6 md:py-20">
          <div className="mb-10 max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1.5 text-xs font-medium text-accent-900">
              <i className="ri-guide-line" />
              安装指引
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              四步开启陪伴
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              从下载到第一句问候，过程简单得不需要说明书。
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
            {steps.map((step, index) => (
              <div key={step.title} className="flex flex-col">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-primary-500 font-heading text-lg font-bold text-background-50">
                    {index + 1}
                  </span>
                  <i className={`${step.icon} text-2xl text-primary-500`} />
                </div>
                <h3 className="mt-5 font-heading text-base font-bold text-foreground-950 md:text-lg">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-10">
            {trustItems.map((item) => (
              <div key={item.title} className="border-t-2 border-primary-200 pt-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-900">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}